import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipboardCheck, ArrowRight, X } from 'lucide-react';
import { API } from '@/App';

/**
 * Sits at the top of the dashboard until practitioner onboarding is finished.
 * Only shows for someone who was actually invited, and disappears for good once
 * their profile is in — the notification bell is easy to miss.
 */
export default function PractitionerOnboardingBanner({ placement = 'layout' }) {
  const [status, setStatus] = useState(null);
  // Hidden for the rest of the session, not for good — they still have to finish.
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('onboardingBannerHidden') === '1'
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onOnboardingPage = pathname.startsWith('/dashboard/practitioner-onboarding');
  // The portal renders its own copy inside its overlay, so the layout must stand down
  // there or the banner exists twice — once visible, once buried under the overlay.
  const onPortal = pathname.startsWith('/dashboard/portal');
  const wrongPlacement = placement === 'layout' ? onPortal : !onPortal;

  useEffect(() => {
    const load = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      axios
        .get(`${API}/practitioner-forms/my-onboarding-status`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(r => setStatus(r.data))
        .catch(() => {});
    };
    load();
    // Signing the agreement or saving the profile fires this, so the tracker moves
    // on immediately instead of waiting for a full page reload.
    window.addEventListener('onboarding-status-changed', load);
    return () => window.removeEventListener('onboarding-status-changed', load);
  }, [pathname]);

  if (!status?.show || dismissed || onOnboardingPage || wrongPlacement) return null;

  return (
    <div
      className="relative mx-auto mb-6 w-full max-w-4xl rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-950/85 to-slate-900/85 p-5 backdrop-blur-sm"
      data-testid="practitioner-onboarding-banner"
    >
      <button
        onClick={() => {
          sessionStorage.setItem('onboardingBannerHidden', '1');
          setDismissed(true);
        }}
        className="absolute right-3 top-3 text-gray-500 hover:text-gray-300 transition-colors"
        aria-label="Hide for now"
        data-testid="practitioner-onboarding-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex flex-wrap items-center gap-4">
        <div className="rounded-lg bg-emerald-500/15 p-3">
          <ClipboardCheck className="w-6 h-6 text-emerald-300" />
        </div>

        <div className="flex-1 min-w-[220px]">
          <p className="text-base font-semibold text-white">
            Finish setting up your practice
          </p>
          <p className="text-sm text-gray-300 mt-1" data-testid="practitioner-onboarding-next-step">
            Step {status.step} of {status.total_steps} — {status.next_step}
          </p>
          <div className="mt-3 flex gap-1.5" aria-hidden="true">
            {Array.from({ length: status.total_steps }).map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-12 rounded-full ${
                  i < status.step - 1
                    ? 'bg-emerald-400'
                    : i === status.step - 1
                    ? 'bg-emerald-400/50'
                    : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
        </div>

        <button
          onClick={() => navigate(status.link)}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-emerald-950 transition-colors hover:bg-emerald-400"
          data-testid="practitioner-onboarding-banner-cta"
        >
          {status.agreement_signed ? 'Finish my profile' : 'Start onboarding'}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
